import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {CheckCircle, XCircle, Activity} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {AutoAssignRuleType} from '@/types/auto-assign-rule'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {AutoAssignRuleControllerRulesEndpoint} from '@/types/endpoint-contracts'
import {permissions} from '@/lib/permissions'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {Pagination} from '@/components/ui/pagination'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {TriggerFullEvaluationButton} from '@/components/auto-assign/TriggerFullEvaluationButton'
import {CONCEPT_ICON} from '@/lib/concepts'

/**
 * Auto-Assign Rules list page — Phase C.2 unified search migration (issue 342.07).
 * Replaces the legacy SearchInput + activeOnly Popover/Checkbox with a single
 * {@link UnifiedSearchBar} driven by the
 * {@link AutoAssignRuleControllerRulesEndpoint} contract.
 *
 * Spec invariants this component upholds:
 *   • Zero implicit default filters — an empty URL shows every rule.
 *   • One request per submit / chip removal.
 *   • `q` matches the rule's @Searchable name field via the
 *     SearchableContractRegistry path.
 */
interface AutoAssignRulesProps {
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

export function AutoAssignRules({embedded, ownedByMe, emptyStateTitle}: AutoAssignRulesProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.autoAssignRules.pageTitle)
    const navigate = useNavigate()
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: AutoAssignRuleControllerRulesEndpoint,
        sortable: true,
        defaultSortBy: AutoAssignRuleControllerRulesEndpoint.sort.NAME,
        defaultSortDir: SortDir.ASC,
    })

    const {listParams, title, emptyMessage} = useEmbeddedListProps({
        contract: AutoAssignRuleControllerRulesEndpoint,
        ownedByMe,
        baseQueryString: queryString,
        emptyStateTitle,
        defaultTitle: i18n.autoAssignRules.allRules,
        createPermission: permissions.autoAssignRules.create,
    })

    const {data: rulesData} = useQuery({
        queryKey: ['auto-assign-rules', queryString, page, pageSize, ownedByMe],
        queryFn: () => services.autoAssignRules.list(listParams),
    })

    const rules = rulesData?.content || []
    const totalItems = rulesData?.totalElements || 0
    const totalPages = rulesData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const getRiskBadgeVariant = (risk: number | null) => {
        if (risk === null) return BadgeVariant.OUTLINE
        if (risk > 75) return BadgeVariant.DESTRUCTIVE
        return BadgeVariant.OUTLINE
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.autoAssignRule}
            title={title}
            count={totalItems}
            description={`${i18n.autoAssignRules.ruleCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="auto-assign-rules-page"
            embedded={embedded}
            actions={ownedByMe ? undefined : (
                <>
                    <TriggerFullEvaluationButton/>
                    <Button variant="outline" onClick={() => navigate(routes.accessGovernance.rules.executions())} data-testid="btn-execution-history">
                        <Icon icon={Activity} size="md" className="mr-2"/>
                        {i18n.autoAssignRules.executionHistory}
                    </Button>
                </>
            )}
            filters={
                <UnifiedSearchBar
                    contract={AutoAssignRuleControllerRulesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.autoAssignRules.searchByName}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination page={page} pageSize={pageSize} totalItems={totalItems} totalPages={totalPages} onPageChange={setPage} onPageSizeChange={setPageSize} pageSizeOptions={pageSizeOptions}/>
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-priority">{i18n.autoAssignRules.priority}</TableHead>
                        <TableHead data-testid="col-type">{i18n.autoAssignRules.columnType}</TableHead>
                        <TableHead data-testid="col-target">{i18n.autoAssignRules.columnTarget}</TableHead>
                        <TableHead data-testid="col-owner">{i18n.autoAssignRules.owner}</TableHead>
                        <TableHead data-testid="col-risk">{i18n.autoAssignRules.risk}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rules.map((rule) => (
                        <ClickableTableRow key={rule.id} to={routes.accessGovernance.rules.detail(rule.id)}>
                            <TableCell className="font-medium">
                                <TextLink to={routes.accessGovernance.rules.detail(rule.id)} stopPropagation testId={`link-rule-detail-${rule.id}`}>{rule.name}</TextLink>
                                {rule.description && (<MutedText size="sm" block className="truncate max-w-xs">{rule.description}</MutedText>)}
                            </TableCell>
                            <TableCell>
                                {rule.active ? (
                                    <Badge variant={BadgeVariant.SUCCESS} className="gap-1 w-fit"><Icon icon={CheckCircle} size="xs"/>{i18n.common.active}</Badge>
                                ) : (
                                    <Badge variant={BadgeVariant.SECONDARY} className="gap-1 w-fit"><Icon icon={XCircle} size="xs"/>{i18n.common.inactive}</Badge>
                                )}
                            </TableCell>
                            <TableCell><Badge variant={BadgeVariant.OUTLINE}>{rule.priority}</Badge></TableCell>
                            <TableCell data-testid={`type-cell-${rule.id}`}>
                                <Badge variant={BadgeVariant.SECONDARY}>
                                    {rule.ruleType === AutoAssignRuleType.FUNCTION_GROUP
                                        ? i18n.autoAssignRules.functionGroupLabel
                                        : i18n.autoAssignRules.accessProfile}
                                </Badge>
                            </TableCell>
                            <TableCell data-testid={`target-cell-${rule.id}`}>
                                {rule.ruleType === AutoAssignRuleType.FUNCTION_GROUP
                                    ? (rule.functionGroups.length > 0
                                        ? rule.functionGroups.map((fg) => fg.name).join(', ')
                                        : '-')
                                    : i18n.autoAssignRules.targetProfileCount({count: rule.profileCount})}
                            </TableCell>
                            <TableCell>{rule.owners.length > 0 ? rule.owners.map(o => o.ownerPartyName).join(', ') : '-'}</TableCell>
                            <TableCell>
                                {rule.userRisk !== null ? (<Badge variant={getRiskBadgeVariant(rule.userRisk)}>{rule.userRisk}</Badge>) : (<MutedText>-</MutedText>)}
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {rules.length === 0 && (
                        <TableEmptyState colSpan={7} hasFilters={hasActiveFilters} matchMessage={i18n.autoAssignRules.noRulesMatch} emptyMessage={emptyMessage ?? i18n.autoAssignRules.noRulesFound}/>
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
